<script lang="ts" setup>
import { computed, ref, useAttrs } from 'vue'
import EyeIcon from '../icons/EyeIcon.vue'

const props = withDefaults(defineProps<Props>(), {
  label: '',
  id: '',
  placeholder: '',
  modelValue: '',
  error: false,
  errorMessage: '',
  wrapperClass: '',
  isPassword: false,
})

interface Props {
  label?: string
  id?: string
  placeholder?: string
  modelValue?: string
  error?: boolean
  errorMessage?: string
  wrapperClass?: string
  isPassword?: boolean
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'blur', ev: FocusEvent): void
  (e: 'focus', ev: FocusEvent): void
}>()

function generateId(): string {
  if (
    typeof globalThis !== 'undefined' &&
    'crypto' in globalThis &&
    globalThis.crypto?.randomUUID
  ) {
    return globalThis.crypto.randomUUID()
  }

  return `input-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const inputId = ref<string>(props.id || generateId())
const inputType = ref<string>(props.isPassword ? 'password' : 'text')

const model = computed<string>({
  get: () => props.modelValue ?? '',
  set: (val) => emit('update:modelValue', val),
})

const attrs = useAttrs()

const describedBy = computed(() => {
  return props.error && props.errorMessage ? `${inputId.value}-feedback` : undefined
})

function togglePassword(): void {
  if (!props.isPassword) return
  if (inputType.value === 'password') {
    inputType.value = 'text'
  } else {
    inputType.value = 'password'
  }
}
</script>

<template>
  <div :class="wrapperClass">
    <label v-if="props.label" :for="inputId" class="form-label">
      {{ props.label }}
    </label>

    <div class="input-wrap" :class="props.isPassword ? 'has-password' : ''">
      <input
        v-bind="attrs"
        :id="inputId"
        v-model="model"
        :type="inputType"
        :placeholder="props.placeholder"
        class="form-control"
        :class="{ 'is-invalid': props.error }"
        :aria-invalid="props.error ? 'true' : 'false'"
        :aria-describedby="describedBy"
        @blur="emit('blur', $event)"
        @focus="emit('focus', $event)"
      />
      <button v-if="props.isPassword" type="button" @click="togglePassword">
        <EyeIcon />
      </button>
    </div>

    <div
      v-if="props.error && props.errorMessage"
      :id="`${inputId}-feedback`"
      class="invalid-feedback d-block"
    >
      {{ props.errorMessage }}
    </div>
  </div>
</template>
